import classNames from 'classnames'
import { useContext, useMemo, useState } from 'react'

import { Button } from 'core-components/library/button'
import {
  DragNDropFileUploader,
  IDragNDropFileUploaderProps,
  IFileSettings,
} from 'core-components/library/dragNDropFileUploader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Select } from 'core-components/library/selects'
import { Tooltip } from 'core-components/library/tooltip'
import { FlexGridWrapperContext } from 'core-components/sitebuilder/layout/context/FlexGridWrapperContext'
import stylesItem from 'core-components/sitebuilder/layout/FlexItem.module.scss'
import { PinOutContext } from 'core-components/sitebuilder/layout/flexItem/pinOutPortal/context/PinOutContext'
import { IFlexItem } from 'core-components/sitebuilder/models/flexGrid/IFlexItem'
import { IPageWidget, IWidget } from 'core-components/sitebuilder/models/IWidget'
import { BaseCrud, Static } from 'core-components/types'

import styles from './Header.module.scss'

enum MenuClickHandlerType {
  FullScreen = 'FullScreen',
  Edit = 'Edit',
  Remove = 'Remove',
  HideHeader = 'HideHeader',
}

export interface IProps {
  widget: IWidget
  pageWidget?: IPageWidget
  allowEdit: boolean
  allowPinOut: boolean
  pinOut: boolean
  onChangeScale?: (newScale: number, i: string) => void
  onClickEdit?: () => void
  onClickRemove?: () => void
  onClickFullScreen?: () => void
  onClickHideHeader?: () => void
  title: string
  onToggleColumnWidth: (bool?: boolean) => void
  onImportSettings: (item: IFlexItem) => void
  onExportSettings: () => void
  isFullScreen: boolean
  onCloseFullScreen: () => void
  color: string
}

export const WidgetHeader = ({
  widget,
  allowEdit,
  allowPinOut,
  pinOut,
  onClickEdit,
  onClickRemove,
  onClickFullScreen,
  onClickHideHeader,
  title,
  onToggleColumnWidth,
  onImportSettings,
  onExportSettings,
  isFullScreen,
  onCloseFullScreen,
  color,
}: IProps) => {
  const { canEditSettings } = useContext(FlexGridWrapperContext)
  const [showFileDialog, setShowFileDialog] = useState(false)
  const pinOutConsume = useContext(PinOutContext)

  const onClickHandler = (type: MenuClickHandlerType) => {
    switch (type) {
      case MenuClickHandlerType.FullScreen:
        return onClickFullScreen && onClickFullScreen()
      case MenuClickHandlerType.Edit:
        return onClickEdit && onClickEdit()
      case MenuClickHandlerType.Remove:
        return onClickRemove && onClickRemove()
      case MenuClickHandlerType.HideHeader:
        return onClickHideHeader && onClickHideHeader()
    }
  }

  const menuCrud = useMemo(() => {
    const data = []
    data.push({
      id: 'importws',
      title: 'Import Widget settings',
      callback: () => {
        setShowFileDialog(true)
      },
    })

    data.push({
      id: 'exportws',
      title: 'Export Widget settings',
      callback: () => {
        onExportSettings()
      },
    })
    return new BaseCrud({
      fetcher: new Static(data),
      keyField: 'id',
      defaultPaging: {
        page: 0,
        pageSize: 6,
      },
    })
  }, [])

  return (
    <>
      <header
        className={classNames(styles.header, 'widget-header')}
        onMouseDown={(event) => {
          if ((event.target as HTMLElement).className === 'kbase kbase-pushpin-alt') {
            event.preventDefault()
            event.stopPropagation()
            return
          }
          event.stopPropagation()
          pinOutConsume.moveWidget(pinOut)
        }}
        onMouseUp={(event) => {
          if ((event.target as any).className === 'kbase kbase-pushpin-alt') {
            event.preventDefault()
            event.stopPropagation()
            return
          }
          event.stopPropagation()
          if (pinOut) {
            pinOutConsume.moveWidget(false)
          }
        }}
      >
        <div className={styles.header__separator} style={{ backgroundColor: color }}></div>
        <div className={styles.content}>
          <p className={styles.title} style={{ color }}>
            {title && !title.startsWith('cell-') ? (
              <>
                {widget.iconClass && (
                  <i className={classNames(styles.title__icon, widget.iconClass)} />
                )}
                {title}
              </>
            ) : (
              <>
                {widget.iconClass && (
                  <i className={classNames(styles.title__icon, widget.iconClass)} />
                )}
                {widget.title}
              </>
            )}
          </p>
        </div>

        <div className={styles.actions}>
          {allowPinOut && (
            <i
              className={'kbase kbase-pushpin-alt'}
              style={{ color: pinOut ? '' : 'gray' }}
              onClick={(e: any) => {
                e.stopPropagation()
                pinOutConsume.setPinOut()
                onToggleColumnWidth()
              }}
            />
          )}

          {/* TODO: delete? */}
          {/*{widget.isScalable ? (*/}
          {/*  <Dropdown*/}
          {/*    className={classNames(styles.actions__item, styles.scale)}*/}
          {/*    label={'Scale'}*/}
          {/*    source={*/}
          {/*      new BaseCrud({*/}
          {/*        fetcher: new Static([*/}
          {/*          { id: '100', value: '100%' },*/}
          {/*          { id: '90', value: '90%' },*/}
          {/*          { id: '80', value: '80%' },*/}
          {/*          { id: '70', value: '70%' },*/}
          {/*          { id: '60', value: '60%' },*/}
          {/*          { id: '50', value: '50%' },*/}
          {/*          { id: '40', value: '40%' },*/}
          {/*          { id: '30', value: '30%' },*/}
          {/*          { id: '20', value: '20%' },*/}
          {/*          { id: '10', value: '10%' },*/}
          {/*        ]),*/}
          {/*        keyField: 'id',*/}
          {/*      })*/}
          {/*    }*/}
          {/*    caption={'value'}*/}
          {/*    value={`${pageWidget?.scale || widget.scale || ''}`}*/}
          {/*    onChange={(value) => onChangeScale && onChangeScale(+value, widget.i)}*/}
          {/*  />*/}
          {/*) : null}*/}

          {isFullScreen ? (
            <Button
              variant={'text'}
              color={'grey'}
              iconButton={'kbase kbase-close'}
              onClick={() => onCloseFullScreen()}
            />
          ) : (
            <>
              {canEditSettings ? (
                <Tooltip tipContent={() => <div>Remove widget</div>}>
                  <Button
                    iconButton={'kbase kbase-delete'}
                    variant={'text'}
                    color={'danger'}
                    onClick={() => onClickHandler(MenuClickHandlerType.Remove)}
                  />
                </Tooltip>
              ) : null}
              {widget.allowWindowMode ? (
                <Tooltip tipContent={() => <div>Open full screen</div>}>
                  <Button
                    iconButton={'kbase kbase-full-screen'}
                    variant={'text'}
                    onClick={() => onClickHandler(MenuClickHandlerType.FullScreen)}
                  />
                </Tooltip>
              ) : null}
              {allowEdit ? (
                <Tooltip tipContent={() => <div>Edit widget</div>}>
                  <Button
                    iconButton={'kbase kbase-edit'}
                    variant={'text'}
                    onClick={() => onClickHandler(MenuClickHandlerType.Edit)}
                  />
                </Tooltip>
              ) : null}

              <Select
                source={menuCrud}
                isSelect
                popupWidth={'byContent'}
                caption={'title'}
                onClick={(item) => {
                  item.callback
                    ? item.callback()
                    : console.log('Calling undefined function on', item)
                }}
              >
                <Button variant={'text'} color={'grey'} iconButton={'hpicon hpicon-ellipsis-v'} />
              </Select>
            </>
          )}
        </div>
      </header>

      <ModalDialog<IDragNDropFileUploaderProps>
        size={'sm'}
        show={showFileDialog}
        content={(dialogProps) => (
          <DragNDropFileUploader
            {...dialogProps}
            changeFileLoader={(filesInfo) => {
              dialogProps.closeModal()
              dialogProps.changeFileLoader(filesInfo)
            }}
          />
        )}
        onClose={() => {
          setShowFileDialog(false)
        }}
        contentProps={{
          changeFileLoader: (filesInfo: IFileSettings[]) => {
            const reader = new FileReader()
            reader.onload = (e: any) => {
              try {
                const result = JSON.parse(e.target.result || '{}')
                onImportSettings(result)
              } catch (e) {
                console.log(e)
              }
            }
            reader.readAsText(filesInfo[0].file)
          },
        }}
      />
    </>
  )
}
